<template>
  <v-container class="d-flex justify-center align-center pa-4" :class="{ 'theme--dark': $vuetify.theme.dark }">
    <v-card class="cash-opening-card rounded-xl elevation-6 w-100" max-width="440" :dark="$vuetify.theme.dark">

      <!-- HEADER horizontal compacto -->
      <div class="primary pa-4 d-flex align-center position-relative overflow-hidden header-pattern">
        <div class="bg-overlay"></div>
        <v-icon size="40" color="white" class="mr-3 relative-z opacity-90">mdi-cash-register</v-icon>
        <div class="relative-z">
          <h2 class="text-h6 font-weight-black white--text mb-0 lh-tight">Apertura de Caja</h2>
          <p class="white--text opacity-80 mb-0 text-caption">
            Ingresa el monto inicial para comenzar el turno
          </p>
        </div>
      </div>

      <v-card-text class="pa-4">
        <v-form ref="openForm" v-model="isValid" @submit.prevent="start">

          <!-- USER INFO -->
          <v-alert color="primary" text rounded="lg" class="mb-4 d-flex align-center px-3 py-2 user-alert">
            <v-avatar color="primary" size="32" class="mr-3">
              <v-icon dark size="18">mdi-account</v-icon>
            </v-avatar>
            <div>
              <div class="caption text-uppercase font-weight-bold opacity-70">Operador de Turno</div>
              <div class="text-body-1 font-weight-black primary--text lh-tight">{{ user }}</div>
            </div>
          </v-alert>

          <!-- AMOUNT INPUT -->
          <div class="caption text-uppercase font-weight-bold text-center mb-2 letter-spacing-1 text--secondary">
            Fondo en Efectivo
          </div>
          <v-text-field
            v-model="initialAmount"
            :rules="[rules.required, rules.positive]"
            type="number"
            prefix="₡"
            placeholder="0.00"
            outlined
            rounded
            class="amount-input"
            hide-details="auto"
            autofocus
            @keyup.enter="start"
            color="primary"
          ></v-text-field>

          <!-- EXCHANGE RATE REFERENCES (GOMETA) -->
          <v-expand-transition>
            <v-row v-if="bccrCompra > 0" class="mt-4 mb-2" dense justify="center">
              <v-col cols="5" class="text-center">
                <v-chip label small color="success" outlined class="font-weight-bold">
                  BCCR Compra: ₡{{ bccrCompra }}
                </v-chip>
              </v-col>
              <v-col cols="5" class="text-center">
                <v-chip label small color="error" outlined class="font-weight-bold">
                  BCCR Venta: ₡{{ bccrVenta }}
                </v-chip>
              </v-col>
            </v-row>
          </v-expand-transition>

          <!-- OPERATIVE EXCHANGE RATE -->
          <div class="caption text-uppercase font-weight-bold text-center mt-2 mb-1 letter-spacing-1 text--secondary">
             Tipo de Cambio Operativo ($)
          </div>
          <v-text-field
            v-model="tdc"
            type="number"
            outlined
            rounded
            dense
            prefix="₡"
            placeholder="0.00"
            color="primary"
            class="centered-input font-weight-bold"
            hide-details
          ></v-text-field>

        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-btn
          :disabled="!isValid || loadingExchange"
          color="primary"
          block
          rounded
          depressed
          class="open-btn"
          @click="start"
          height="44"
          :loading="loadingExchange"
        >
          <span class="mr-2 font-weight-black text-body-1">ABRIR CAJA</span>
          <v-icon right size="20">mdi-arrow-right-circle</v-icon>
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  props: {
    user: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isValid: false,
      initialAmount: null,
      tdc: 0,
      bccrCompra: 0,
      bccrVenta: 0,
      loadingExchange: false,
      rules: {
        required: (value) => value !== null && value !== "" || "El monto es obligatorio.",
        positive: (value) => Number(value) >= 0 || "El monto no puede ser negativo.",
      },
    };
  },
  mounted() {
    this.fetchExchangeRate();
  },
  methods: {
    async fetchExchangeRate() {
      this.loadingExchange = true;
      try {
        const response = await axios.get("api/General/getTipoCambioGometa");
        if (response.data) {
          this.bccrCompra = parseFloat(response.data.compra);
          this.bccrVenta = parseFloat(response.data.venta);
          // Por defecto trabajamos con la compra
          this.tdc = this.bccrCompra;
        }
      } catch (error) {
        console.error("Error fetching exchange rate:", error);
      } finally {
        this.loadingExchange = false;
      }
    },
    start() {
      if (this.$refs.openForm.validate()) {
        this.$emit("open-cash", [this.initialAmount, this.tdc]);
      }
    },
  }
};
</script>

<style scoped>
.cash-opening-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(128, 128, 128, 0.1);
}

.theme--dark .cash-opening-card {
  background-color: #1e1e1e !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.theme--dark .amount-input >>> input {
  color: #ffffff !important;
}

.position-relative {
  position: relative;
}

.relative-z {
  position: relative;
  z-index: 2;
}

.lh-tight {
  line-height: 1.2;
}

.opacity-70 {
  opacity: 0.7;
}

.opacity-80 {
  opacity: 0.8;
}

.opacity-90 {
  opacity: 0.9;
}

.user-alert {
  border: 1px solid rgba(25, 118, 210, 0.2) !important;
}

.bg-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.15) 100%);
  z-index: 1;
}

.header-pattern {
  background-image: radial-gradient(rgba(255,255,255,0.1) 10%, transparent 10%),
                    radial-gradient(rgba(255,255,255,0.1) 10%, transparent 10%);
  background-size: 20px 20px;
  background-position: 0 0, 10px 10px;
}

.amount-input >>> input {
  font-size: 1.6rem !important;
  font-weight: 900 !important;
  text-align: center;
  letter-spacing: 2px;
}

.amount-input >>> .v-input__prefix {
  font-size: 1.3rem;
  font-weight: 700;
  opacity: 0.7;
  align-self: center;
  padding-bottom: 0;
}

.amount-input >>> .v-input__control > .v-input__slot {
  min-height: 56px !important;
}

.amount-input >>> fieldset {
  border-width: 2px !important;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

.open-btn {
  letter-spacing: 1px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.open-btn:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.15);
}
</style>
